import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { COMPANY } from "@/data";

const CGV = () => (
  <>
    <SEO
      title="Conditions Générales de Vente"
      description="Conditions générales de vente et de service de VistaWash. Detailing automobile à Châtelet, Belgique."
      path="/cgv"
    />

    <section className="pt-40 pb-16 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nos engagements</span>
          <h1 className="font-impact text-5xl md:text-7xl lg:text-8xl leading-[0.9] mt-6">
            CONDITIONS <br />
            <span className="text-[#D90000]">GÉNÉRALES.</span>
          </h1>
          <p className="text-white/60 leading-relaxed mt-8 max-w-2xl">
            Les présentes conditions régissent l'ensemble des prestations réalisées par VistaWash. Elles sont acceptées sans réserve par le client dès la validation du devis.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="pb-32 px-6 md:px-12">
      <div className="max-w-4xl mx-auto space-y-16">
        <Block number="01" title="Objet">
          <p>
            Les présentes Conditions Générales de Vente (CGV) définissent les droits et obligations de VistaWash et de ses clients dans le cadre de la fourniture de prestations de nettoyage, detailing, polissage et protection céramique de véhicules.
          </p>
        </Block>

        <Block number="02" title="Devis et commande">
          <p>
            Tout devis est établi gratuitement, sur mesure, en fonction de l'état du véhicule et de la prestation demandée. Il est valable <strong className="text-white">30 jours</strong> à compter de sa date d'émission.
          </p>
          <p className="mt-3">
            La commande est ferme et définitive dès validation écrite du devis par le client (email, WhatsApp ou signature).
          </p>
        </Block>

        <Block number="03" title="Prix">
          <p>
            Les prix sont indiqués en euros, TVA incluse (TVA belge 21%). Les tarifs affichés sur le site sont donnés à titre indicatif et peuvent varier selon la taille, l'état et les spécificités du véhicule.
          </p>
        </Block>

        <Block number="04" title="Rendez-vous et annulation">
          <p>
            Les prestations sont réalisées sur rendez-vous à l'atelier VistaWash. Toute annulation doit être signalée au minimum <strong className="text-white">48 heures</strong> avant la date convenue.
          </p>
          <p className="mt-3">
            En cas d'absence ou d'annulation tardive (moins de 24h), un forfait de <strong className="text-white">50 €</strong> pourra être facturé.
          </p>
        </Block>

        <Block number="05" title="Réalisation des prestations">
          <p>
            La durée des prestations varie de <strong className="text-white">2 à 8 heures</strong> selon la formule choisie. Pour les traitements céramiques, une immobilisation de 24 à 48h peut être nécessaire.
          </p>
          <p className="mt-3">
            Le client s'engage à présenter un véhicule vidé de ses effets personnels. VistaWash décline toute responsabilité en cas de perte ou détérioration d'objets laissés dans le véhicule.
          </p>
        </Block>

        <Block number="06" title="Paiement">
          <p>
            Le paiement s'effectue à la remise du véhicule, par :
          </p>
          <ul className="list-none space-y-2 mt-3">
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Espèces</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Virement bancaire</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Paiement mobile (Payconiq, Bancontact)</li>
          </ul>
          <p className="mt-4">
            Pour les prestations supérieures à 500 €, un acompte de 30% peut être demandé à la commande.
          </p>
        </Block>

        <Block number="07" title="Garanties">
          <p>
            Les traitements céramiques bénéficient d'une garantie constructeur variable selon le produit choisi (2 à 9 ans). Cette garantie couvre les défauts d'adhérence du revêtement dans des conditions normales d'utilisation et d'entretien.
          </p>
          <p className="mt-3">
            La garantie est conditionnée au respect des consignes d'entretien fournies (lavage manuel, produits pH neutre, éviter les stations automatiques).
          </p>
        </Block>

        <Block number="08" title="Responsabilité">
          <p>
            VistaWash s'engage à réaliser ses prestations avec le plus grand soin. Sa responsabilité est limitée aux dommages directs causés par une faute prouvée dans l'exécution de la prestation.
          </p>
          <p className="mt-3">
            VistaWash ne saurait être tenu responsable des défauts préexistants du véhicule (rayures profondes, corrosion, décollement de peinture, etc.), qui sont systématiquement signalés au client avant la prestation.
          </p>
        </Block>

        <Block number="09" title="Réclamations">
          <p>
            Toute réclamation doit être formulée dans les <strong className="text-white">48 heures</strong> suivant la remise du véhicule, par email à <a href={`mailto:${COMPANY.email}`} className="text-[#D90000] hover:underline">{COMPANY.email}</a>, accompagnée de photos.
          </p>
        </Block>

        <Block number="10" title="Droit applicable">
          <p>
            Les présentes CGV sont régies par le droit belge. En cas de litige, une solution amiable sera recherchée en priorité. À défaut, le litige sera soumis aux tribunaux de Charleroi, Belgique.
          </p>
        </Block>
      </div>
    </section>
  </>
);

const Block = ({ number, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    data-testid={`cgv-block-${number}`}
    className="border-l-2 border-[#D90000] pl-8"
  >
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-impact text-2xl text-[#D90000]">{number}</span>
      <h2 className="font-impact text-2xl md:text-3xl uppercase tracking-wide">{title}</h2>
    </div>
    <div className="space-y-3 text-white/70 leading-relaxed">{children}</div>
  </motion.div>
);

export default CGV;
